import { Alert, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { PersonOutline as PersonIcon, SwapHoriz as SwapIcon } from '@mui/icons-material';
import useSWR from 'swr';
import { useState } from 'react';
import { PageShell } from '@/layout/PageShell';
import { useAuth } from '@/auth/AuthContext';
import { explorer, type ExplorerRunResult } from '@/api/explorer';
import { me } from '@/api/tenant';
import { useToast } from '@/components/Toaster';
import { StatusPill } from '@/components/StatusPill';
import { PatientPicker } from '@/components/PatientPicker';
import { useActivePatient } from '@/utils/activePatient';
import { M3ConsentForm } from './M3ConsentForm';

/**
 * Standalone Consent Request page under Integration.
 *
 * Wraps {@link M3ConsentForm} as a top-level route so tenant developers
 * can drive the M3 flow without diving into the API Explorer's per-
 * endpoint workbench. Same proxy underneath ({@code explorer.run}) →
 * same audit logging + decoded errors + capture pointers.
 */
export function IntegrationConsentPage() {
  const theme = useTheme();
  const toast = useToast();
  const { claims } = useAuth();
  const { data: tenantSelf } = useSWR('me-tenant', () => me.tenant());
  const [patient] = useActivePatient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState<ExplorerRunResult | null>(null);

  return (
    <PageShell
      kicker="TENANT · INTEGRATION · CONSENT"
      title="Consent Request"
      subtitle="HIU consent flow. Patient gets a prompt in their PHR app; once granted, records become available for fetch via the consent list + FHIR artifact endpoints."
      width="wide"
      actions={tenantSelf && (
        <StatusPill tone={tenantSelf.environment === 'PRODUCTION' ? 'success' : 'info'}>
          {tenantSelf.environment}
        </StatusPill>
      )}
    >
      {patient ? (
        <Alert
          severity="success" variant="outlined"
          sx={{ mb: 2, borderRadius: theme.radius.sm + 'px' }}
          action={<Button size="small" startIcon={<SwapIcon fontSize="small" />} onClick={() => setPickerOpen(true)}>Change</Button>}
        >
          <Typography variant="body2" fontWeight={500}>Patient: {patient.name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {patient.abhaAddress} · UHID {patient.memberId}
          </Typography>
        </Alert>
      ) : (
        <Alert
          severity="info" variant="outlined"
          sx={{ mb: 2, borderRadius: theme.radius.sm + 'px' }}
          action={<Button size="small" startIcon={<PersonIcon fontSize="small" />} onClick={() => setPickerOpen(true)}>Pick patient</Button>}
        >
          No patient selected. Pick a registered patient (or onboard one via ABHA Creation) — the form below will auto-fill.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 360px' }, gap: 3 }}>
        <Box sx={{ p: 3, border: `1px solid ${theme.palette.border.subtle}`, borderRadius: theme.radius.md + 'px', bgcolor: theme.palette.surface.card }}>
          <M3ConsentForm
            key={patient?.abhaAddress ?? 'no-patient'}
            context={{
              tenantId: String(tenantSelf?.tenantId ?? claims?.tenantId ?? ''),
              abhaAddress: patient?.abhaAddress ?? '',
              hiuId: String(tenantSelf?.tenantId ?? ''),
              requesterName: String(claims?.name ?? claims?.username ?? ''),
              requesterRegNo: String(claims?.username ?? ''),
            }}
            submitting={running}
            onSubmit={async (body) => {
              setRunning(true);
              try {
                const r = await explorer.run({ apiCode: 'M3.CONSENT.REQUEST.V3', body });
                setLastResult(r);
                if (r.ok) toast.success(`Consent requested · ${r.httpStatus} · ${r.durationMs}ms`);
                else toast.error(`Consent failed · ${r.httpStatus}`, r.decodedError ?? 'See result panel.');
              } catch (e) { toast.error('Run failed', (e as Error).message); }
              finally { setRunning(false); }
            }}
          />
        </Box>

        <Stack spacing={2}>
          {lastResult && (
            <Box sx={{ p: 2, border: `1px solid ${theme.palette.border.subtle}`, borderRadius: theme.radius.md + 'px', bgcolor: theme.palette.surface.card }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <StatusPill tone={lastResult.ok ? 'success' : 'danger'}>
                  {lastResult.httpStatus} {lastResult.ok ? 'OK' : 'FAIL'}
                </StatusPill>
                <Typography variant="caption" color="text.secondary">{lastResult.durationMs} ms</Typography>
              </Stack>
              {!lastResult.ok && lastResult.decodedError && (
                <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
                  <strong>What this means:</strong> {lastResult.decodedError}
                </Alert>
              )}
              {lastResult.captured && Object.keys(lastResult.captured).length > 0 && (
                <Box sx={{ mb: 1.5, p: 1.5, bgcolor: theme.palette.surface.sunken, borderRadius: theme.radius.sm + 'px' }}>
                  <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 0.5 }}>Captured</Typography>
                  {Object.entries(lastResult.captured).map(([k, v]) => (
                    <Typography key={k} variant="caption" sx={{ display: 'block', fontFamily: 'JetBrains Mono, monospace' }}>
                      <strong>{k}:</strong> {String(v)}
                    </Typography>
                  ))}
                </Box>
              )}
              <Box component="pre" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, bgcolor: theme.palette.surface.sunken, p: 1.5, m: 0, borderRadius: theme.radius.sm + 'px', maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(lastResult.response, null, 2)}
              </Box>
            </Box>
          )}
          {!lastResult && (
            <Alert severity="info" variant="outlined">
              Submit a consent request to see the captured <code>consentRequestId</code> here. Use it on the Consent List endpoint in the API Explorer to poll for GRANTED status.
            </Alert>
          )}
        </Stack>
      </Box>

      <PatientPicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
    </PageShell>
  );
}
